import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, Send, Trash2, Eye } from 'lucide-react';
import { format } from 'date-fns';

interface DraftVersion {
  id: string;
  version_number: number;
  version_type: 'estimate' | 'change_order';
  created_at: string;
  reason_for_change?: string;
  change_description?: string;
  total_amount: number;
}

interface DraftVersionBannerProps {
  draftVersions: DraftVersion[];
  onSendVersion: (versionId: string) => void;
  onDeleteVersion: (versionId: string) => void;
  onViewVersion: (versionId: string) => void;
  isLoading?: boolean;
}

const DraftVersionBanner: React.FC<DraftVersionBannerProps> = ({
  draftVersions,
  onSendVersion,
  onDeleteVersion,
  onViewVersion,
  isLoading = false
}) => {
  if (!draftVersions || draftVersions.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      {draftVersions.map((version) => (
        <Card key={version.id} className="border-orange-200 bg-orange-50 overflow-hidden">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-start gap-2 sm:gap-3">
              <AlertCircle className="h-5 w-5 text-orange-500 mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1 space-y-2">
                <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                  <Badge variant="secondary" className="bg-orange-100 text-orange-800 shrink-0">
                    Draft {version.version_type === 'change_order' ? 'Change Order' : 'Version'} v{version.version_number}
                  </Badge>
                  <span className="text-xs sm:text-sm text-muted-foreground">
                    Created {format(new Date(version.created_at), 'dd MMMM yyyy')}
                  </span>
                </div>
                
                <div>
                  <p className="font-medium text-orange-900 text-sm sm:text-base">
                    {version.version_type === 'change_order' 
                      ? 'Change order ready to send' 
                      : 'New estimate version ready to send'
                    }
                  </p>
                  <p className="text-xs sm:text-sm text-orange-700">
                    Total: R{version.total_amount.toLocaleString()}
                  </p>
                </div>

                {version.reason_for_change && (
                  <p className="text-xs sm:text-sm text-muted-foreground break-words">
                    <strong>Reason:</strong> {version.reason_for_change}
                  </p>
                )}

                {version.change_description && (
                  <p className="text-xs sm:text-sm text-muted-foreground break-words">
                    <strong>Changes:</strong> {version.change_description}
                  </p>
                )}

                <div className="flex items-center gap-2 pt-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onViewVersion(version.id)}
                    disabled={isLoading}
                    className="h-9 text-xs px-3 flex-1 sm:flex-none sm:h-9 sm:text-sm sm:px-3"
                  >
                    <Eye className="w-3.5 h-3.5 mr-1 shrink-0" />
                    View
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onDeleteVersion(version.id)}
                    disabled={isLoading}
                    className="h-9 text-xs px-3 flex-1 sm:flex-none sm:h-9 sm:text-sm sm:px-3 text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1 shrink-0" />
                    Delete
                  </Button>
                  
                  <Button
                    size="sm"
                    onClick={() => onSendVersion(version.id)}
                    disabled={isLoading}
                    className="h-9 text-xs px-3 flex-1 sm:flex-none sm:h-9 sm:text-sm sm:px-3 bg-orange-600 hover:bg-orange-700"
                  >
                    <Send className="w-3.5 h-3.5 mr-1 shrink-0" />
                    Send
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default DraftVersionBanner;